import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Building2, Rocket, Wrench, Calculator, ChevronRight, Sparkles } from "lucide-react";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    icon: Zap,
    accent: "border-slate-300",
    badge: null,
    price: 99,
    users: "Up to 5 Users",
    tagline: "Perfect for small businesses moving away from paper forms and spreadsheets.",
    features: [
      "Job Management",
      "Employee Management",
      "Time Clock (Clock In / Out)",
      "Weekly Timesheets",
      "Basic Forms & Checklists",
      "Photo Uploads",
      "Mobile Access",
      "Built-In Tutorial Videos",
      "Email Support",
    ],
    extraUserPrice: 15,
  },
  {
    id: "growth",
    name: "Growth",
    icon: Building2,
    accent: "border-orange-500",
    badge: "MOST POPULAR",
    price: 199,
    users: "Up to 10 Users",
    tagline: "For growing businesses wanting greater control over operations and compliance.",
    features: [
      "Everything in Starter, plus:",
      "Machine & Equipment Tracking",
      "Employee Certificates & Tickets Register",
      "Certificate Expiry Tracking",
      "Damage & Fault Reporting",
      "Advanced Forms & Compliance Management",
      "Employee Time History",
      "Long Shift Notifications",
      "Job Cost Tracking",
      "Priority Email Support",
    ],
    extraUserPrice: 12,
  },
  {
    id: "professional",
    name: "Professional",
    icon: Rocket,
    accent: "border-slate-900",
    badge: "FULL VISIBILITY",
    price: 349,
    users: "Up to 20 Users",
    tagline: "Designed for established companies wanting complete operational visibility.",
    features: [
      "Everything in Growth, plus:",
      "Advanced Reporting Dashboard",
      "Custom Company Branding",
      "Multiple Management Access Levels",
      "Custom Workflow Configuration",
      "API Integration Capability",
      "Xero Integration Ready",
      "Priority Support",
      "Quarterly System Review",
      "Dedicated Account Manager",
    ],
    extraUserPrice: 10,
  },
];

const SHADOWQUOTE = [
  { name: "Single Trade", price: 49, features: ["Quote Builder", "PDF Quote Generation", "Saved Quote Templates", "Customer Database"] },
  { name: "Multi Trade", price: 99, features: ["Multiple Trade Templates", "Advanced Cost Calculations", "Labour & Equipment Cost Tracking", "Margin & Profit Tracking"] },
  { name: "Unlimited", price: 149, features: ["Unlimited Trade Templates", "AI Assisted Quoting", "Advanced Reporting", "Future Trade Packs Included"] },
];

const SETUP = [
  { name: "Standard Setup", price: "$750", features: ["Company Setup", "Staff Setup", "Equipment Setup", "Initial Training", "Data Import Assistance"] },
  { name: "Advanced Setup", price: "$1,500+", features: ["Full Company Configuration", "Custom Workflows", "Custom Forms", "Staff Training Sessions", "System Tailoring"] },
];

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-orange-400 mb-3">
            <Sparkles className="w-4 h-4" /> Built for tradies, riggers & operators
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading uppercase tracking-tight">ShadowOps Pricing</h1>
          <p className="text-slate-300 mt-3 text-base max-w-2xl">
            Pick a plan that suits your crew. Upgrade, downgrade, or cancel anytime — no lock-in contracts. All prices in AUD, ex-GST.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 rounded-md font-bold text-sm uppercase tracking-wide transition-colors"
            data-testid="pricing-cta-login"
          >
            Already a customer? Sign in <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`relative bg-white border-4 ${p.accent} rounded-md p-6 flex flex-col`}
                data-testid={`plan-${p.id}`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest rounded">
                    {p.badge}
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-6 h-6 text-orange-500" />
                  <h3 className="text-2xl font-bold uppercase tracking-tight font-heading">{p.name}</h3>
                </div>
                <div className="mb-1">
                  <span className="text-5xl font-bold text-slate-900">${p.price}</span>
                  <span className="text-slate-500 text-sm">/month</span>
                </div>
                <div className="text-xs font-semibold uppercase text-slate-500 mb-3">{p.users}</div>
                <p className="text-sm text-slate-600 mb-4">{p.tagline}</p>
                <ul className="space-y-2 mb-5 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className={f.endsWith(":") ? "font-semibold" : ""}>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-slate-100 pt-3 text-xs text-slate-500">
                  Additional users: <span className="font-bold text-slate-700">${p.extraUserPrice}/user/month</span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">No lock-in. Upgrade or cancel with 30 days' notice. Prices ex-GST.</p>
      </section>

      {/* ShadowQuote add-ons */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-bold uppercase tracking-wide text-slate-900">Optional Add-On — ShadowQuote</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Construction & trade quoting system. Layer on top of any plan.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SHADOWQUOTE.map((sq) => (
            <div key={sq.name} className="bg-white border-2 border-purple-200 rounded-md p-5" data-testid={`shadowquote-${sq.name.toLowerCase().replace(/\s/g, "-")}`}>
              <h4 className="text-sm font-bold uppercase text-purple-700 tracking-wide mb-1">{sq.name}</h4>
              <div className="mb-3">
                <span className="text-3xl font-bold text-slate-900">${sq.price}</span>
                <span className="text-slate-500 text-sm">/month</span>
              </div>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {sq.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Setup */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-2">
          <Wrench className="w-5 h-5 text-slate-700" />
          <h2 className="text-lg font-bold uppercase tracking-wide text-slate-900">Setup & Onboarding</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">One-off fee covered before your first month, to get your company live and crew trained.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SETUP.map((s) => (
            <div key={s.name} className="bg-white border-2 border-slate-200 rounded-md p-5" data-testid={`setup-${s.name.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-baseline justify-between mb-3">
                <h4 className="text-sm font-bold uppercase tracking-wide text-slate-900">{s.name}</h4>
                <span className="text-2xl font-bold text-slate-900">{s.price}</span>
              </div>
              <ul className="space-y-1.5 text-sm text-slate-700">
                {s.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Custom Dev */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-slate-900 text-white rounded-md p-6 sm:p-8">
          <h3 className="text-2xl font-bold uppercase tracking-tight font-heading">Customisation & Development</h3>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            Need a feature built just for your crew? Custom integrations, reports, or workflows — we build them per-company. Fixed-price quote provided before work commences.
          </p>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-4xl font-bold text-orange-400">$100</span>
            <span className="text-slate-300 text-sm">/hour <span className="text-slate-500">or fixed-price quotation</span></span>
          </div>
        </div>
      </section>

      {/* Early adopter */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-orange-50 border-2 border-orange-300 rounded-md p-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-orange-700 font-bold mb-2">
            <Sparkles className="w-4 h-4" /> Early Adopter Offer
          </div>
          <h3 className="text-xl font-bold text-slate-900">Joining during launch?</h3>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl mx-auto">
            First 3–5 businesses get discounted pricing — <span className="font-bold">Starter $99 · Growth $149 · Pro $299 · Setup $500</span> — and priority influence on the next features we ship.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="mt-5 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-bold text-sm uppercase tracking-wide"
            data-testid="early-adopter-cta"
          >
            Get In Touch
          </button>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-12 text-center text-xs text-slate-400">
        ShadowOps · Construction Business Management · Built in Australia · Prices in AUD, ex-GST
      </footer>
    </div>
  );
};

export default PricingPage;
